import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ImageUploadField, resolveImageUrl } from "@/components/admin/ImageUploadField";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { type: "image", url: "", youtube_id: "", title: "", description: "" };

export function GalleryManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/gallery").then((res) => setItems(res.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ type: item.type, url: item.url, youtube_id: item.youtube_id || "", title: item.title, description: item.description || "" }); setOpen(true); };

  const handleSave = async () => {
    try {
      const payload = form.type === "video" && form.youtube_id
        ? { ...form, url: `https://img.youtube.com/vi/${form.youtube_id}/hqdefault.jpg` }
        : form;
      if (editing) {
        await api.put(`/admin/gallery/${editing.id}`, payload);
      } else {
        await api.post("/admin/gallery", payload);
      }
      toast.success("Saved successfully.");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save.");
    }
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Deleted.");
    load();
  };

  return (
    <div data-testid="gallery-manager">
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="gallery-add-button" className="rounded-none bg-accent text-accent-foreground">
          <Plus className="h-4 w-4 mr-2" /> Add Item
        </Button>
      </div>
      <div className="bg-background border border-border divide-y divide-border">
        {items.map((item) => (
          <div key={item.id} data-testid={`gallery-manager-row-${item.id}`} className="flex items-center gap-4 p-4">
            <img src={item.type === "video" ? `https://img.youtube.com/vi/${item.youtube_id}/hqdefault.jpg` : resolveImageUrl(item.url)} alt={item.title} className="w-16 h-16 object-cover" />
            <div className="flex-1">
              <p className="font-medium">{item.title}</p>
              <p className="text-xs text-muted-foreground uppercase">{item.type}</p>
            </div>
            <Button size="icon" variant="outline" className="rounded-none" data-testid={`gallery-edit-${item.id}`} onClick={() => openEdit(item)}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="outline" className="rounded-none" data-testid={`gallery-delete-${item.id}`} onClick={() => handleDelete(item.id)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-muted-foreground text-sm">No gallery items yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Gallery Item</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="gallery-form-type-select" className="rounded-none mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="image">Image</SelectItem><SelectItem value="video">YouTube Video</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input data-testid="gallery-form-title-input" className="rounded-none mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            {form.type === "image" ? (
              <div>
                <Label>Image</Label>
                <div className="mt-1.5">
                  <ImageUploadField testId="gallery-form-url-input" value={form.url} onChange={(v) => setForm({ ...form, url: v })} />
                </div>
              </div>
            ) : (
              <div>
                <Label>YouTube Video ID</Label>
                <Input data-testid="gallery-form-youtube-input" placeholder="e.g. M7lc1UVf-VE" className="rounded-none mt-1.5" value={form.youtube_id} onChange={(e) => setForm({ ...form, youtube_id: e.target.value })} />
              </div>
            )}
            <div>
              <Label>Description</Label>
              <Input data-testid="gallery-form-description-input" className="rounded-none mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <Button onClick={handleSave} data-testid="gallery-form-save-button" className="w-full rounded-none bg-accent text-accent-foreground">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
